import { useEffect, useState } from 'react'
import type { CheckboxOptionType } from 'antd'
import { Button, CheckboxGroup, Modal, Spin } from '@/components/ui'
import type { AttendeeStatus } from '../types'

/**
 * Shape returned by the QR export count endpoint. All four status keys are
 * always present; `total` is the sum across every status (informational).
 */
export interface QrExportCounts {
  counts: Record<AttendeeStatus, number>
  total: number
}

interface QrExportStatusModalProps {
  /** Whether the status-picker modal is visible. */
  open: boolean
  /** Close the modal without exporting. */
  onClose: () => void
  /** Run the QR export scoped to the chosen statuses. */
  onConfirm: (statuses: AttendeeStatus[]) => void
  /** Reflects the in-flight export so buttons show progress / stay disabled. */
  loading?: boolean
  /**
   * Fetch per-status export counts for the current search/filters. Supplied by
   * the parent so the query-string / api client stay co-located with the page.
   * Optional: when omitted the modal renders without counts (unchanged UX).
   */
  fetchCounts?: () => Promise<QrExportCounts>
}

/**
 * The 4 attendee statuses offered in the picker, in display order. Labels mirror
 * the canonical `AttendeeStatusBadge` statusMap (see
 * `components/ui/Badge/Badge.tsx`). Kept as local constants (typed to the
 * `AttendeeStatus` union) so the option values stay in sync with the shared type
 * at compile time.
 */
const STATUS_LABELS: Record<AttendeeStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

/** Every status key, in display order — also the default selection (all checked). */
const ALL_STATUSES: AttendeeStatus[] = ['pending', 'approved', 'rejected', 'cancelled']

/** Set of valid status keys, used to narrow the CheckboxGroup's `unknown[]`. */
const STATUS_KEYS = new Set<string>(ALL_STATUSES)

/** Type guard narrowing an unknown checkbox value back to `AttendeeStatus`. */
const isAttendeeStatus = (value: unknown): value is AttendeeStatus =>
  typeof value === 'string' && STATUS_KEYS.has(value)

/** Placeholder shown for a per-status count while counts are loading / unavailable. */
const COUNT_PLACEHOLDER = '—'

/**
 * QrExportStatusModal
 *
 * A multi-select status picker shown before running the QR codes (ZIP) export.
 * All four statuses are checked by default; the user may uncheck any. When
 * `fetchCounts` is provided the modal shows a live COUNT of how many attendees
 * will be exported per status and in total, updating as checkboxes toggle.
 *
 * The Export button is disabled when nothing is selected, or when counts are
 * known and the selection resolves to zero attendees. A count-fetch failure is
 * surfaced but never blocks the export. Selection resets to all-checked and
 * counts are (re)fetched each time the modal is opened.
 */
export default function QrExportStatusModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  fetchCounts,
}: QrExportStatusModalProps) {
  const [selected, setSelected] = useState<AttendeeStatus[]>(ALL_STATUSES)
  const [counts, setCounts] = useState<Record<AttendeeStatus, number> | null>(null)
  const [countsLoading, setCountsLoading] = useState(false)
  const [countsError, setCountsError] = useState(false)

  // On open: reset to the all-checked default and (re)fetch per-status counts.
  // A `cancelled` flag guards against applying stale results after the modal is
  // closed / unmounted or the effect re-runs.
  useEffect(() => {
    if (!open) return

    setSelected(ALL_STATUSES)

    if (!fetchCounts) {
      setCounts(null)
      setCountsError(false)
      setCountsLoading(false)
      return
    }

    let cancelled = false
    setCounts(null)
    setCountsError(false)
    setCountsLoading(true)

    fetchCounts()
      .then((result) => {
        if (cancelled) return
        setCounts(result.counts)
        setCountsError(false)
      })
      .catch(() => {
        if (cancelled) return
        setCounts(null)
        setCountsError(true)
      })
      .finally(() => {
        if (cancelled) return
        setCountsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, fetchCounts])

  // Checkbox options carry a live count suffix, e.g. "Approved (42)". While
  // counts are unavailable (loading / error / not provided) a placeholder shows.
  const options: CheckboxOptionType<AttendeeStatus>[] = ALL_STATUSES.map((value) => ({
    value,
    label: `${STATUS_LABELS[value]} (${counts ? counts[value] : COUNT_PLACEHOLDER})`,
  }))

  const hasSelection = selected.length > 0
  const countsReady = counts !== null
  const selectedCount = selected.reduce((sum, status) => sum + (counts?.[status] ?? 0), 0)

  // Disable Export when nothing is selected, an export is in flight, or counts
  // are known and the selection resolves to zero attendees. A count-fetch
  // failure (counts === null) must NOT disable export as long as ≥1 status is
  // selected — the backend still scopes correctly by the chosen statuses.
  const nothingToExport = countsReady && selectedCount === 0
  const exportDisabled = loading || !hasSelection || nothingToExport

  const exportLabel = countsReady ? `Export (${selectedCount})` : 'Export'

  return (
    <Modal
      title="Export QR codes by status"
      open={open}
      onClose={onClose}
      confirmLoading={loading}
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm min-h-[22px]" aria-live="polite">
            {fetchCounts ? (
              countsLoading ? (
                <span className="inline-flex items-center gap-2 text-gray-500">
                  <Spin size="small" /> Calculating…
                </span>
              ) : countsError ? (
                <span className="text-gray-400">Couldn&apos;t load counts</span>
              ) : (
                <span className="font-medium text-gray-700">
                  {selectedCount} {selectedCount === 1 ? 'attendee' : 'attendees'} will be exported
                </span>
              )
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <Button key="cancel" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              key="export"
              variant="primary"
              onClick={() => onConfirm(selected)}
              disabled={exportDisabled}
              loading={loading}
            >
              {exportLabel}
            </Button>
          </div>
        </div>
      }
    >
      <div className="mb-2">
        <p className="text-sm text-gray-500 mb-4">
          Select which attendee statuses to include.
        </p>
        <CheckboxGroup
          className="flex flex-col gap-2"
          options={options}
          value={selected}
          onChange={(values) => setSelected(values.filter(isAttendeeStatus))}
        />
      </div>
    </Modal>
  )
}
